import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OnboardingHeader from "../components/OnboardingHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { OnboardingImage } from "../components/OnboardingImage";
import { OnboardingProgress } from "../components/OnboardingProgress";
import { useOnboardingNavigation } from "../hooks/useOnboardingNavigation";
import StepOne from "../components/step-one";
import StepTwo, { type StepTwoSelection } from "../components/step-two";
import StepThree from "../components/step-three";
import StepFour, { type PurchaseFrequency } from "../components/step-four";
import StepFive from "../components/step-five";
import StepSix from "../components/step-six";
import { getOnboardingSteps } from "../types";
import {
  ONBOARDING_FALLBACK_COMUNA_ID,
  readOnboardingState,
  type OnboardingState,
  writeOnboardingState,
} from "../types/onboarding-state";

const stepComponents: Record<number, React.FC> = {
  1: StepOne,
  6: StepSix,
};

const onboardingSteps = getOnboardingSteps();

/** Default empty draft for the step-2 selector (nothing selected yet). */
const EMPTY_DRAFT_SELECTION: StepTwoSelection = {
  cityId: "",
  cityName: "",
  locationId: "",
  locationName: "",
  isFallback: false,
};

/**
 * Validates the step-2 draft before allowing the wizard to advance.
 *
 * Rules (per spec "Invalid draft blocks persistence and advancement"):
 *   - a comuna must be selected;
 *   - if the selected comuna is the fallback, no sector is required;
 *   - any other comuna requires a sector.
 */
const isDraftSelectionValid = (selection: StepTwoSelection): boolean => {
  if (!selection.cityId) {
    return false;
  }
  if (selection.isFallback) {
    return true;
  }
  return Boolean(selection.locationId);
};

/** Error message shown when the user clicks Siguiente without a valid draft. */
const STEP_TWO_INVALID_DRAFT_MESSAGE =
  "Selecciona una comuna y un sector para continuar";

/** Error message shown when the user clicks Siguiente on step 4 without selecting a frequency. */
const STEP_FOUR_INVALID_DRAFT_MESSAGE =
  "Selecciona la frecuencia con la que haces tus compras";

/** Error message shown when the user clicks Siguiente on step 5 without selecting a monthly spend. */
const STEP_FIVE_INVALID_DRAFT_MESSAGE =
  "Selecciona tu gasto mensual en supermercado para continuar";

/**
 * Onboarding wizard page.
 *
 * Owns the per-step draft state for steps that require validation
 * (step 2 location selection, step 4 purchase frequency). Each draft is
 * validated on Siguiente and persisted as part of a typed `OnboardingState`
 * via the navigation hook's `setOnboardingData` before the wizard advances.
 * The wizard never POSTs to `updateLocationEndPoint` and never writes
 * `localStorage.userData` — selections live in their own
 * `carriapp:onboarding` slot.
 */
export const OnboardingPage = () => {
  const {
    currentStep,
    totalSteps,
    onboardingData,
    next,
    prev,
    complete,
    setOnboardingData,
  } = useOnboardingNavigation();
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;

  // Refs para evitar que el popstate handler dependa del ciclo de render.
  const stepRef = useRef(currentStep);
  stepRef.current = currentStep;
  const prevRef = useRef(prev);
  prevRef.current = prev;

  // Intercepta el botón back del navegador: si el usuario está más allá del
  // paso 1, lo redirige al paso anterior en vez de salir del onboarding.
  // Usa `window.history.pushState` para crear una entrada "trampa" que el
  // back del navegador siempre poppea en la misma URL, permitiéndonos
  // interceptar el evento `popstate` y llamar a `prev()` manualmente.
  useEffect(() => {
    window.history.pushState(null, "");

    const handlePopState = () => {
      if (stepRef.current > 1) {
        prevRef.current();
        // Solo re-trampa si después de prev aún estamos más allá del paso 1.
        // Si llegamos al paso 1, dejamos que el próximo back navegue fuera.
        if (stepRef.current > 2) {
          window.history.pushState(null, "");
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const [draftSelection, setDraftSelection] = useState<StepTwoSelection>(
    EMPTY_DRAFT_SELECTION,
  );
  const [draftError, setDraftError] = useState<string | null>(null);

  // Step 4 draft — initialized lazily from the persisted
  // `onboardingData.purchase_frequency` so a remount resumes with the
  // previously selected frequency pre-checked on the radio group.
  const [draftFrequency, setDraftFrequency] = useState<
    PurchaseFrequency | null
  >(() => onboardingData.purchase_frequency);
  const [draftFrequencyError, setDraftFrequencyError] = useState<string | null>(
    null,
  );

  // Step 5 draft — initialized lazily from the persisted
  // `onboardingData.monthly_spend` so a remount resumes with the
  // previously selected spend pre-positioned on the slider. The slider
  // always renders a numeric value internally; the page-owned draft is
  // `null` until the user actually moves the thumb, so the page can
  // detect "untouched" vs "value at the minimum anchor".
  const [draftSpend, setDraftSpend] = useState<number | null>(
    () => onboardingData.monthly_spend,
  );
  const [draftSpendError, setDraftSpendError] = useState<string | null>(null);

  const handleStepTwoSelectionChange = useCallback(
    (nextSelection: StepTwoSelection) => {
      setDraftSelection(nextSelection);
      // Clear any stale validation error as soon as the user changes the
      // draft.
      if (draftError) {
        setDraftError(null);
      }
    },
    [draftError],
  );

  const handleStepFourSelectionChange = useCallback(
    (nextFrequency: PurchaseFrequency) => {
      setDraftFrequency(nextFrequency);
      // Clear any stale step-4 validation error so the user sees the
      // affordance react immediately to their selection.
      if (draftFrequencyError) {
        setDraftFrequencyError(null);
      }
    },
    [draftFrequencyError],
  );

  const handleStepFiveValueChange = useCallback(
    (nextSpend: number) => {
      setDraftSpend(nextSpend);
      // Clear any stale step-5 validation error so the user sees the
      // affordance react immediately to their selection.
      if (draftSpendError) {
        setDraftSpendError(null);
      }
    },
    [draftSpendError],
  );

  /**
   * Builds the typed `OnboardingState` payload that should be persisted
   * to `localStorage[ONBOARDING_STORAGE_KEY]` when leaving step 2.
   *
   * `currentStep` is bumped to the NEXT step so the payload reflects the
   * post-transition state. `location_id` and `location_name` stay in lock
   * step — both `null` when the selection is the fallback (no sector) so
   * the persisted shape is internally consistent.
   */
  const buildStepTwoPersistedPayload = useCallback(
    (selection: StepTwoSelection): OnboardingState => {
      const hasLocation = Boolean(selection.locationId);
      return {
        ...onboardingData,
        currentStep: currentStep + 1,
        city_id: selection.isFallback
          ? ONBOARDING_FALLBACK_COMUNA_ID
          : Number(selection.cityId),
        city_name: selection.cityName,
        location_id: hasLocation ? Number(selection.locationId) : null,
        location_name: hasLocation ? selection.locationName : null,
        isFallback: selection.isFallback,
      };
    },
    [currentStep, onboardingData],
  );

  /**
   * Builds the typed `OnboardingState` payload that should be persisted
   * when leaving step 4.
   *
   * Spreads the current `onboardingData` so city / location fields from
   * the previous step survive the transition untouched, bumps
   * `currentStep` to the NEXT step, and writes the new
   * `purchase_frequency` value typed.
   */
  const buildStepFourPersistedPayload = useCallback(
    (frequency: PurchaseFrequency): OnboardingState => {
      const latestDraft = readOnboardingState() ?? onboardingData;
      return {
        ...latestDraft,
        currentStep: currentStep + 1,
        purchase_frequency: frequency,
      };
    },
    [currentStep, onboardingData],
  );

  /**
   * Builds the typed `OnboardingState` payload that should be persisted
   * when leaving step 5.
   *
   * Spreads the current `onboardingData` so city / location /
   * purchase_frequency fields from previous steps survive the
   * transition untouched, bumps `currentStep` to the NEXT step, and
   * writes the new `monthly_spend` value typed.
   */
  const buildStepFivePersistedPayload = useCallback(
    (spend: number): OnboardingState => {
      const latestDraft = readOnboardingState() ?? onboardingData;
      return {
        ...latestDraft,
        currentStep: currentStep + 1,
        monthly_spend: spend,
      };
    },
    [currentStep, onboardingData],
  );

  const handlePrimaryAction = useCallback(() => {
    // Step 6 owns its own completion: it must NOT navigate to /store via
    // this page-level primary action because the spec requires the user
    // to authenticate before leaving onboarding. StepSix observes
    // useAuth().isLogged and navigates itself once auth resolves, so
    // any click here would short-circuit the auth gate.
    if (currentStep === 6) {
      return;
    }
    if (isLastStep) {
      complete();
      return;
    }
    if (currentStep === 2) {
      if (!isDraftSelectionValid(draftSelection)) {
        setDraftError(STEP_TWO_INVALID_DRAFT_MESSAGE);
        return;
      }
      const payload = buildStepTwoPersistedPayload(draftSelection);
      // Mirror to localStorage directly so a fresh remount sees the new
      // payload even if React state batching delays the hook update. The
      // hook's setOnboardingData keeps React state in sync.
      writeOnboardingState(payload);
      setOnboardingData(payload);
      setDraftError(null);
      return;
    }
    if (currentStep === 4) {
      if (!draftFrequency) {
        setDraftFrequencyError(STEP_FOUR_INVALID_DRAFT_MESSAGE);
        return;
      }
      const payload = buildStepFourPersistedPayload(draftFrequency);
      writeOnboardingState(payload);
      setOnboardingData(payload);
      setDraftFrequencyError(null);
      return;
    }
    if (currentStep === 5) {
      if (draftSpend === null) {
        setDraftSpendError(STEP_FIVE_INVALID_DRAFT_MESSAGE);
        return;
      }
      const payload = buildStepFivePersistedPayload(draftSpend);
      writeOnboardingState(payload);
      setOnboardingData(payload);
      setDraftSpendError(null);
      return;
    }
    next();
  }, [
    isLastStep,
    complete,
    currentStep,
    draftSelection,
    draftFrequency,
    draftSpend,
    buildStepTwoPersistedPayload,
    buildStepFourPersistedPayload,
    buildStepFivePersistedPayload,
    setOnboardingData,
    next,
  ]);

  const renderStepContent = () => {
    if (currentStep === 2) {
      return (
        <StepTwo
          onSelectionChange={handleStepTwoSelectionChange}
          error={draftError ?? undefined}
        />
      );
    }
    if (currentStep === 3) {
      return (
        <StepThree
          cityName={onboardingData.city_name}
          locationName={onboardingData.location_name}
          isFallback={onboardingData.isFallback}
          locationId={onboardingData.location_id}
        />
      );
    }
    if (currentStep === 4) {
      return (
        <StepFour
          value={draftFrequency ?? ""}
          onValueChange={handleStepFourSelectionChange}
          error={draftFrequencyError ?? undefined}
        />
      );
    }
    if (currentStep === 5) {
      return (
        <StepFive
          value={draftSpend}
          onValueChange={handleStepFiveValueChange}
          purchaseFrequency={onboardingData.purchase_frequency}
          error={draftSpendError ?? undefined}
        />
      );
    }
    const StepContent = stepComponents[currentStep] ?? StepOne;
    return <StepContent />;
  };

  const currentStepData = onboardingSteps.find(
    (step) => step.id === currentStep,
  );
  const dummyImage = currentStepData?.image ?? "";

  return (
    <div className="flex flex-col min-h-[100dvh] bg-background w-full">
      <main
        className="grid w-full flex-1 min-h-0 gap-8  px-4 lg:grid-cols-2 lg:gap-12"
        aria-label="Asistente de onboarding"
      >
        <div className="flex flex-col gap-6 max-w-lg mx-auto w-full">
          <div className="py-10 flex flex-col gap-6">
            <OnboardingHeader />
            {
            (!isFirstStep)&&(
            <OnboardingProgress
              currentStep={currentStep}
              totalSteps={totalSteps -1}
            />
            )
            }
           {renderStepContent()}
            {!isFirstStep && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={prev}
                className="self-start text-muted-foreground"
              >
                <ArrowLeft className="mr-2" />
                Anterior
              </Button>
            )}
            {!isLastStep && (
              <Button
                type="button"
                size="lg"
                onClick={handlePrimaryAction}
                className="h-12 text-base font-semibold self-start px-4 w-full"
              >
                {isFirstStep ? "¡Comencemos!" : isLastStep ? "Registrate y crea tu lista" : "Siguiente"}
              </Button>
            )}
          </div>
        </div>
        <OnboardingImage src={dummyImage} alt={`Paso ${currentStep}`} />
      </main>
    </div>
  );
};
